import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { teamAPI, cronAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  UserPlus, Trash, Shield, PencilSimple, Copy, Clock, Lightning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLE_COLORS = { admin: 'nx-tag-red', editor: 'nx-tag-blue', viewer: 'nx-tag-gray' };
const ROLE_LABELS = { admin: 'Admin', editor: 'Editor', viewer: 'Viewer' };

export default function TeamPage() {
  const { user, isAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [cronStatus, setCronStatus] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [t, c] = await Promise.all([teamAPI.list(), cronAPI.scheduleStatus()]);
      setMembers(t.data);
      setCronStatus(c.data);
    } catch { /* non-admin won't have access */ }
    finally { setLoading(false); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      const res = await teamAPI.invite({ email: inviteEmail, role: inviteRole });
      toast.success(`Invited ${inviteEmail} as ${inviteRole}. Temp password: ${res.data.temp_password}`);
      navigator.clipboard?.writeText(res.data.temp_password);
      setDialogOpen(false);
      setInviteEmail('');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Invite failed'); }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await teamAPI.updateRole(memberId, newRole);
      toast.success('Role updated');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const handleRemove = async (memberId) => {
    if (!window.confirm('Revoke this member\'s access? Their account record stays, and their current sessions stop working.')) return;
    try {
      await teamAPI.remove(memberId);
      toast.success('Member removed');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Remove failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="team-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              Settings
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Team management, roles &amp; scheduling</p>
          </div>
        </div>

        <Tabs defaultValue="team" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
            <TabsTrigger value="team" data-testid="tab-team" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Team</TabsTrigger>
            <TabsTrigger value="cron" data-testid="tab-cron" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Scheduling</TabsTrigger>
          </TabsList>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-4">
            {/* Role Legend */}
            <div className="nx-metric-card rounded-lg">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">
                <Shield size={18} className="inline mr-2 text-[var(--accent)]" weight="duotone" />
                Role Permissions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { role: 'Admin', desc: 'Full access: manage team, approve, delete, configure schedules', color: 'var(--error)' },
                  { role: 'Editor', desc: 'Can run agents, approve recommendations, create content, generate playbooks', color: 'var(--accent)' },
                  { role: 'Viewer', desc: 'Read-only: view dashboards, reports, and analytics', color: 'var(--text-tertiary)' },
                ].map(r => (
                  <div key={r.role} className="p-3 rounded border border-[var(--border-light)]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                      <span className="text-sm text-[var(--text-primary)] font-medium">{r.role}</span>
                    </div>
                    <p className="text-xs text-[var(--text-secondary)]">{r.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Members Table */}
            <div className="nx-metric-card rounded-lg" data-testid="team-members-table">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">
                  Team Members ({members.length})
                </h3>
                {isAdmin && (
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="invite-member-btn" className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                        <UserPlus size={14} className="mr-2" /> Invite Member
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="invite-dialog">
                      <DialogHeader>
                        <DialogTitle className="font-display">Invite Team Member</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Email</label>
                          <Input data-testid="invite-email-input" value={inviteEmail}
                            onChange={e => setInviteEmail(e.target.value)}
                            placeholder="team@agency.com" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                        </div>
                        <div>
                          <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Role</label>
                          <Select value={inviteRole} onValueChange={setInviteRole}>
                            <SelectTrigger data-testid="invite-role-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                              <SelectItem value="admin" className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">Admin</SelectItem>
                              <SelectItem value="editor" className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">Editor</SelectItem>
                              <SelectItem value="viewer" className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button data-testid="send-invite-btn" onClick={handleInvite} className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                          Send Invite
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              <table className="nx-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Joined</th>{isAdmin && <th>Actions</th>}</tr>
                </thead>
                <tbody>
                  {members.map(m => {
                    const removed = m.membership_status === 'removed';
                    return (
                    <tr key={m.id}>
                      <td className="!text-[var(--text-primary)] font-medium">
                        {m.name} {m.id === user?.id && <span className="text-xs text-[var(--text-tertiary)] ml-1">(you)</span>}
                      </td>
                      <td className="font-mono text-xs">{m.email}</td>
                      <td>
                        {removed ? (
                          <span className="nx-tag nx-tag-gray" data-testid={`member-removed-${m.id}`}>Removed</span>
                        ) : isAdmin && m.id !== user?.id ? (
                          <Select value={m.role || 'admin'} onValueChange={v => handleRoleChange(m.id, v)}>
                            <SelectTrigger data-testid={`role-select-${m.id}`} className="w-28 h-7 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                              <SelectItem value="admin" className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">Admin</SelectItem>
                              <SelectItem value="editor" className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">Editor</SelectItem>
                              <SelectItem value="viewer" className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`nx-tag ${ROLE_COLORS[m.role || 'admin']}`}>{ROLE_LABELS[m.role || 'admin']}</span>
                        )}
                      </td>
                      <td className="font-mono text-xs">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</td>
                      {isAdmin && (
                        <td>
                          {m.id !== user?.id && !removed && (
                            <button data-testid={`remove-member-${m.id}`} onClick={() => handleRemove(m.id)}
                              className="p-1.5 rounded hover:bg-[var(--error-bg)] text-[var(--text-secondary)] hover:text-[var(--error)] transition-colors duration-200">
                              <Trash size={14} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Scheduling Tab */}
          <TabsContent value="cron" className="space-y-4">
            <div className="nx-metric-card rounded-lg" data-testid="cron-status-panel">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <Clock size={18} className="inline mr-2 text-[var(--accent)]" weight="duotone" />
                Cron-Ready Endpoints
              </h3>
              <p className="text-xs text-[var(--text-secondary)] mb-4 leading-relaxed">
                These endpoints are designed for external cron schedulers (e.g., cron jobs, AWS EventBridge, GitHub Actions).
                Call them with your auth token on a schedule.
              </p>
              {cronStatus && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="p-3 rounded border border-[var(--border-light)]">
                      <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase">Schedules</p>
                      <p className="text-xl font-bold text-[var(--text-primary)] font-mono">{cronStatus.total_schedules}</p>
                    </div>
                    <div className="p-3 rounded border border-[var(--border-light)]">
                      <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase">Enabled</p>
                      <p className="text-xl font-bold text-[var(--success)] font-mono">{cronStatus.enabled}</p>
                    </div>
                    <div className="p-3 rounded border border-[var(--border-light)]">
                      <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase">Daily</p>
                      <p className="text-xl font-bold text-[var(--accent)] font-mono">{cronStatus.daily}</p>
                    </div>
                    <div className="p-3 rounded border border-[var(--border-light)]">
                      <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase">Weekly</p>
                      <p className="text-xl font-bold text-[var(--warning)] font-mono">{cronStatus.weekly}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(cronStatus.cron_endpoints || {}).map(([key, endpoint]) => (
                      <div key={key} className="flex items-center gap-3 p-3 rounded border border-[var(--border-light)]">
                        <Lightning size={14} className="text-[var(--accent)] flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-[var(--text-primary)] font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                          <p className="text-xs text-[var(--text-tertiary)] font-mono">{endpoint}</p>
                        </div>
                        <button onClick={() => { navigator.clipboard?.writeText(endpoint); toast.success('Copied'); }}
                          className="p-1.5 rounded hover:bg-[var(--canvas-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-200">
                          <Copy size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
